import React, { useCallback } from 'react'

import { Header, Icon, Text, VStack } from 'govwell-ui'
import { AIChatSourceBreakdown } from 'types/graphql'

import { Link, routes } from '@redwoodjs/router'

type SourceColumnProps = {
  icon: React.ComponentProps<typeof Icon>['icon']
  label: string
  count: number
  percentage: number
  iconBgColor: string
  iconColor: string
  emptyAction?: React.ReactNode
}

const SourceColumn = React.memo(
  ({ icon, label, count, percentage, iconBgColor, iconColor, emptyAction }: SourceColumnProps) => (
    <div className="flex flex-1 flex-col items-center gap-2 rounded-lg border border-solid border-gray-200 bg-white p-4">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconBgColor}`}>
        <Icon icon={icon} className={`text-base ${iconColor}`} />
      </div>
      <Text.BodySmall className="font-medium text-gray-500">{label}</Text.BodySmall>
      {count > 0 ? (
        <>
          <Text.BodyLarge className="font-bold">{count}</Text.BodyLarge>
          <Text.BodySmall className="text-gray-400">{percentage}% of total</Text.BodySmall>
        </>
      ) : (
        <>
          <Text.BodyLarge className="font-bold text-gray-300">0</Text.BodyLarge>
          {emptyAction ?? <Text.BodySmall className="text-gray-400">No questions</Text.BodySmall>}
        </>
      )}
    </div>
  )
)

type Props = {
  sourceBreakdown: AIChatSourceBreakdown
  totalQuestions: number
}

const SourcesSection = ({ sourceBreakdown, totalQuestions }: Props) => {
  const pct = useCallback(
    (count: number) => (totalQuestions > 0 ? Math.round((count / totalQuestions) * 100) : 0),
    [totalQuestions]
  )

  return (
    <VStack gap={4}>
      <Header title="Question Sources" subtitle="Where community members are asking questions" />
      <div className="grid w-full grid-cols-3 gap-4">
        <SourceColumn
          icon="FaGlobe"
          label="Services Portal"
          count={sourceBreakdown.servicesPortal}
          percentage={pct(sourceBreakdown.servicesPortal)}
          iconBgColor="bg-emerald-50"
          iconColor="text-emerald-600"
        />
        <SourceColumn
          icon="FaCode"
          label="Website Widget"
          count={sourceBreakdown.websiteWidget}
          percentage={pct(sourceBreakdown.websiteWidget)}
          iconBgColor="bg-purple-50"
          iconColor="text-purple-600"
          emptyAction={
            <Link
              to={routes.aiAssistantSettings()}
              className="text-sm text-blue-600 hover:underline"
            >
              Set up widget
            </Link>
          }
        />
        <SourceColumn
          icon="FaDesktop"
          label="In App"
          count={sourceBreakdown.inApp}
          percentage={pct(sourceBreakdown.inApp)}
          iconBgColor="bg-blue-50"
          iconColor="text-blue-600"
        />
      </div>
    </VStack>
  )
}

export default React.memo(SourcesSection)
